import * as React from "react";
import { Tooltips } from ".";

type DataType = {
  title: string;
  subtitle: string;
  description: JSX.Element;
};

const DATA: DataType[] = [
  {
    title: "1. SIMPLICITY",
    subtitle:
      "Make the idea simple by stripping it to its core, or using an analogy.",
    description: (
      <div className="col-md-8">
        <p>
          Make complex information simple and understandable. Here’s an example
          using hypertension (high blood pressure):
        </p>
        <ul className="bullet-fulcrum bullet-gold">
          <li>
            <p>
              Simple – High blood pressure is like having high pressure in a
              pipe. The pressure causes damage that can’t be seen until it
              bursts the pipe.
            </p>
          </li>
          <li>
            <p>
              Complex – Hypertension is a chronic medical condition in which the
              blood pressure within the arteries is elevated. This leads to an
              increased risk of coronary artery disease.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "2. UNEXPECTEDNESS",
    subtitle:
      "Demand attention by generating interest and curiosity with the element of surprise.",
    description: (
      <div className="col-md-8">
        <p>
          Using surprising facts or figures might help you to do this. For
          example:
        </p>
        <ul className="bullet-fulcrum bullet-gold">
          <li>
            <p>Did you know that 1 in 3 Australians have high cholesterol?</p>
          </li>
          <li>
            <p>
              Did you know that a 600 mL bottle of iced coffee contains 14
              teaspoons of sugar?
            </p>
          </li>
        </ul>
        <p>
          <a
            href="https://www.aihw.gov.au/reports-data/australias-health"
            className="text-reset text-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Australia’s Health 2020
          </a>{" "}
          is a great resource if you’re looking for health statistics.
        </p>
      </div>
    ),
  },
  {
    title: "3. CONCRETENESS",
    subtitle:
      "Ensure the idea means the same to everyone. Make it real, not abstract.",
    description: (
      <div className="col-md-8">
        <p>
          Concrete information is easier to understand and remember. For
          example:
        </p>
        <ul className="bullet-fulcrum bullet-gold">
          <li>
            <p>
              ‘Did you know that a 600 mL bottle of cola drink contains over
              1,000 kJ?’ This statement is abstract as many may be unfamiliar
              with what a kJ is.
            </p>
          </li>
          <li>
            <p>
              ‘Did you know a 600 mL bottle of cola drink contains 16 teaspoons
              of sugar?’ This statement is concrete as a teaspoon is a familiar
              measurement.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "4. CREDIBILITY",
    subtitle:
      "Use a credible source to deliver the idea and always use evidence-based information.",
    description: (
      <div className="col-md-8">
        <p>
          If you are using facts or figures to make your point sticky, ensure
          you are sourcing them from a credible health organisation. Government
          agencies (such as the Department of Health or the National Health and
          Medical Research Council) and non-government organisations (such as
          the Heart Foundation and Cancer Council WA) are great sources of
          credible information.
        </p>
        <p>
          If you are engaging with a health professional to provide education or
          advice to staff, ensure they are university qualified. For example,
          use a dietitian for nutrition information or an exercise physiologist
          for exercise information.
        </p>
      </div>
    ),
  },
  {
    title: "5. EMOTIONS",
    subtitle:
      "Use associations to tie ideas to something people care about, things that resonate with your audience.",
    description: (
      <div className="col-md-8">
        <p>
          Feelings inspire people to act, and we can use this to engage workers
          to care about something they might not have otherwise cared about. For
          example, a scientific study completed in 2012 found that dog owners
          were more likely to participate in physical activity if you told them
          that it was good for the health of their dog, rather than for their
          own health.
        </p>
      </div>
    ),
  },
  {
    title: "6. STORIES",
    subtitle:
      "Encourage mental stimulation and re-enactment through sharing the experience of others.",
    description: (
      <div className="col-md-8">
        <p>
          People often learn through the experience of others, so it’s important
          that you share positive outcomes and good news within the workplace.
          Sharing testimonials and real stories from peers can be a powerful tool
          to motivate and encourage staff to participate in healthy behaviours.
        </p>
      </div>
    ),
  },
];

export const StickyMessage: React.FC<{}> = () => {
  return (
    <div className="row">
      <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-2 d-flex flex-column align-items-center mb-4">
        <h2 className="h1 text-uppercase text-center mb-4">
          Ensuring your health message is "sticky"
        </h2>
        <p>
          Engaging your employees and keeping them interested in their health at
          work can be tricky. ‘Sticky’ messages can help as they are easy to
          understand, are remembered, and have a long lasting impact that can
          change levels of knowledge, attitudes and even behaviours.
        </p>
        <div className="embed-responsive embed-responsive-16by9 mb-4">
          <iframe
            className="embed-responsive-item"
            src="https://www.youtube-nocookie.com/embed/1JvAUr3jMQI"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <p className="video-caption lead text-center">
          So how do you make a message ‘sticky’? Watch how Professor Phil Morgan
          applied the six principles of the ‘stickiness factor’ to help engage
          staff:
          <Tooltips id={25} />
        </p>
      </div>
      <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-2 d-flex flex-column align-items-center">
        {DATA.map(({ title, subtitle, description }) => (
          <div key={title} className="row border-bottom py-3 mb-4">
            <div className="col-md-4 d-flex flex-column text-white">
              <h4>{title}</h4>
              <p>{subtitle}</p>
            </div>
            {description}
          </div>
        ))}
      </div>
    </div>
  );
};
